import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import LottieReact from "lottie-react";

const Lottie = ({ options = {}, eventListeners = [], ariaRole, role, ...rest }) => {
  const {
    animationData,
    loop = true,
    autoplay = true,
    rendererSettings,
    ...otherOptions
  } = options;

  const { height, width, style, ...forwardProps } = rest;
  const lottieRef = useRef(null);

  useEffect(() => {
    const animation = lottieRef.current;
    if (!animation || typeof animation.addEventListener !== "function") return undefined;

    const cleanups = eventListeners
      .filter((listener) => listener?.eventName && typeof listener?.callback === "function")
      .map(({ eventName, callback }) => {
        animation.addEventListener(eventName, callback);
        return () => {
          animation.removeEventListener(eventName, callback);
        };
      });

    return () => {
      cleanups.forEach((cleanup) => cleanup && cleanup());
    };
  }, [eventListeners, animationData]);

  const memoizedStyle = useMemo(() => {
    const computed = { ...style };
    if (height) computed.height = height;
    if (width) computed.width = width;
    return Object.keys(computed).length ? computed : undefined;
  }, [style, height, width]);

  const resolvedRole = role || ariaRole;

  return (
    <LottieReact
      lottieRef={lottieRef}
      animationData={animationData}
      loop={loop}
      autoplay={autoplay}
      rendererSettings={rendererSettings}
      role={resolvedRole}
      style={memoizedStyle}
      {...otherOptions}
      {...forwardProps}
    />
  );
};

Lottie.propTypes = {
  options: PropTypes.shape({
    animationData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    loop: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    autoplay: PropTypes.bool,
    rendererSettings: PropTypes.object,
  }).isRequired,
  eventListeners: PropTypes.arrayOf(
    PropTypes.shape({
      eventName: PropTypes.string,
      callback: PropTypes.func,
    })
  ),
  ariaRole: PropTypes.string,
  role: PropTypes.string,
};

export default Lottie;
